import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router } from "@angular/router";
import { Observable } from "rxjs";
import { map, take } from "rxjs/operators";
import { AuthUser } from "../model/auth-user";
import { AuthUserService } from "./auth-user.service";

@Injectable({
    providedIn: "root",
})
export class AuthGuard implements CanActivate, CanActivateChild {
    constructor(private readonly router: Router, private readonly authUserService: AuthUserService) {}

    canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
        return this.authUserService.getAuthUser().pipe(
            take(1),
            map(authUser => this.hasPermissions(authUser, route))
        );
    }

    canActivateChild(route: ActivatedRouteSnapshot): Observable<boolean> {
        return this.canActivate(route);
    }

    // borrowed from NgxPermissions
    private hasPermissions(authUser: AuthUser, route: ActivatedRouteSnapshot) {
        const permissions = route?.data?.permissions || {};
        if (this.isParameterAvailable(permissions.except)) {
            return this.passingExceptPermissionsValidation(authUser, permissions);
        }
        if (this.isParameterAvailable(permissions.only)) {
            return this.passingOnlyPermissionsValidation(authUser, permissions);
        }
        return true;
    }

    private isParameterAvailable(permission: string[]) {
        return permission?.length > 0;
    }

    private passingExceptPermissionsValidation(authUser: AuthUser, { except, redirectTo }) {
        if (!authUser?.permissions || authUser.permissions.some(permission => except.includes(permission))) {
            if (redirectTo && !this.isRedirectionWithParameters(redirectTo)) {
                this.router.navigate([redirectTo], { queryParamsHandling: "merge" }).catch(_ => null);
            }
            return false;
        }
        return true;
    }

    private passingOnlyPermissionsValidation(authUser: AuthUser, { only, redirectTo }) {
        if (!authUser?.permissions || !only.some(permission => authUser.permissions.includes(permission))) {
            if (redirectTo && !this.isRedirectionWithParameters(redirectTo)) {
                this.router.navigate([redirectTo], { queryParamsHandling: "merge" }).catch(_ => null);
            }
            return false;
        }

        return true;
    }

    private isRedirectionWithParameters(object: { navigationCommands: any; navigationExtras: any }) {
        return !!object.navigationCommands || !!object.navigationExtras;
    }
}
